import { useCallback, useMemo, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SessionsFilterBar } from './SessionsFilterBar';
import { SessionsTable } from './SessionsTable';
import { SessionsTableSkeleton } from './SessionsTableSkeleton';
import { MobileSessionCard } from './MobileSessionCard';
import { MobileCardSkeleton } from '../../../components/common/MobileCardSkeleton/MobileCardSkeleton';
import { ListPageLayout } from '../components/ListPageLayout';
import { SessionV2, SessionsFilterState } from '../../../types/dashboardV2';
import { useSessionsQuery } from '../../../hooks/useDashboardQueries';
import { useListPage } from '../../../hooks/useListPage';
import { useActiveFilterCount } from '../../../hooks/useActiveFilterCount';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import { ROUTES } from '../../../constants/routes';
import {
  fromMobileSessionsFilters,
  toMobileSessionsFilters,
} from './sessionsMobileFilters';

const MobileFilterSheet = lazy(() => import('./MobileSessionsFilterSheet'));

import type { MobileSessionsFilterState } from '../../../types/mobileFilterSheetTypes';

const INITIAL_FILTERS: SessionsFilterState = {
  search: '',
  status: '',
  dateRange: 'Last 30 days',
  stationIds: [],
  maxEnergy: 0,
  maxDurationMinutes: 0,
};

export const SessionsV2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    page,
    rowsPerPage,
    filters,
    queryFilters,
    isFilterSheetOpen,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    refresh: handleRefresh,
    openFilterSheet,
    closeFilterSheet,
  } = useListPage<SessionsFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.SESSIONS,
    urlSync: true,
    numericFilterKeys: ['maxEnergy', 'maxDurationMinutes'],
    arrayFilterKeys: ['stationIds'],
  });

  const {
    data,
    isLoading: loading,
    isFetching,
    error,
  } = useSessionsQuery({ page: page + 1, pageSize: rowsPerPage, filters: queryFilters });

  const total = data?.total ?? 0;

  // All filters (incl. stationIds/maxEnergy/maxDurationMinutes) are applied
  // server-side, so `data.total` matches the filtered set.
  const sessions = data?.data ?? [];

  const handleApplyMobileFilters = useCallback(
    (mobile: MobileSessionsFilterState) =>
      handleFilterChange(fromMobileSessionsFilters(mobile)),
    [handleFilterChange],
  );

  const mobileFiltersForSheet = useMemo(
    () => toMobileSessionsFilters(filters),
    [filters],
  );

  const handleViewDetails = useCallback(
    (session: SessionV2) => {
      navigate(
        `/${ROUTES.DASHBOARD}/sessions/${session.sessionId}/metrics`,
      );
    },
    [navigate],
  );

  const errorMessage = error instanceof Error ? error.message : t('dashboard.sessions.errorLoading');
  const activeFilterCount = useActiveFilterCount(filters, INITIAL_FILTERS);

  return (
    <ListPageLayout
      filterBar={
        <SessionsFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
        />
      }
      showLoading={loading || isFetching}
      isLoading={loading}
      hasError={Boolean(error)}
      errorMessage={errorMessage}
      onRetry={handleRefresh}
      countText={t('dashboard.sessions.displayingOfTotal', { count: sessions.length, total })}
      itemCount={sessions.length}
      emptyState={{ icon: 'electric_car', message: t('dashboard.sessions.noSessions') }}
      table={
        <SessionsTable
          sessions={sessions}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onViewDetails={handleViewDetails}
        />
      }
      tableSkeleton={<SessionsTableSkeleton />}
      mobileTitle={t('dashboard.sidebar.sessions')}
      mobileCountText={t('dashboard.sessions.allSessionsWithCount', { count: total })}
      search={{
        value: filters.search,
        onChange: (search) => handleFilterChange({ search }),
        placeholder: t('dashboard.sessions.searchPlaceholder'),
      }}
      mobileCards={sessions.map((session) => (
        <MobileSessionCard
          key={session.id}
          session={session}
          onViewDetails={handleViewDetails}
        />
      ))}
      mobileSkeleton={<MobileCardSkeleton />}
      pagination={{
        total,
        page,
        rowsPerPage,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
      }}
      filterButton={{ activeCount: activeFilterCount, onOpen: openFilterSheet }}
      isFilterSheetOpen={isFilterSheetOpen}
      filterSheet={
        <MobileFilterSheet
          isOpen={isFilterSheetOpen}
          onClose={closeFilterSheet}
          onApply={handleApplyMobileFilters}
          initialFilters={mobileFiltersForSheet}
        />
      }
    />
  );
};

export default SessionsV2;
